import React, { useState, useRef, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link, Quote, Code } from 'lucide-react';

/**
 * Simple Rich Text Editor compatible with React 19
 * Uses contentEditable with basic formatting toolbar
 */
const RichTextEditor = ({ value, onChange, placeholder = "Write your content here...", height = "200px" }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    // Trigger onChange with updated content
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-gold/20 transition-colors ${active ? 'bg-gold/30 text-burgundy' : 'text-charcoal/70'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="rich-text-editor border border-gold/20 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-cream border-b border-gold/20">
        <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Underline">
          <Underline size={16} />
        </ToolbarButton>
        <div className="w-px bg-gold/20 mx-1" />
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          <ListOrdered size={16} />
        </ToolbarButton>
        <div className="w-px bg-gold/20 mx-1" />
        <ToolbarButton onClick={insertLink} title="Insert Link">
          <Link size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'blockquote')} title="Quote">
          <Quote size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'pre')} title="Code Block">
          <Code size={16} />
        </ToolbarButton>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        dangerouslySetInnerHTML={{ __html: value || '' }}
        className="p-3 outline-none prose prose-sm max-w-none"
        style={{ 
          minHeight: height, 
          fontFamily: "'Inter', sans-serif",
          backgroundColor: 'white'
        }}
        data-placeholder={placeholder}
      />
      
      <style>{`
        .rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #2D2D2D50;
          pointer-events: none;
        }
        .rich-text-editor [contenteditable] {
          word-wrap: break-word;
        }
        .rich-text-editor [contenteditable] a {
          color: #C9A962;
          text-decoration: underline;
        }
        .rich-text-editor [contenteditable] blockquote {
          border-left: 3px solid #C9A962;
          padding-left: 1rem;
          margin-left: 0;
          color: #2D2D2D99;
        }
        .rich-text-editor [contenteditable] pre {
          background: #FAF8F5;
          padding: 0.5rem;
          border-radius: 4px;
          font-family: monospace;
        }
        .rich-text-editor [contenteditable] ul,
        .rich-text-editor [contenteditable] ol {
          padding-left: 1.5rem;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
